import { HTTP_INTERCEPTORS, HttpRequest, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_RIPPLE_GLOBAL_OPTIONS, RippleGlobalOptions } from '@angular/material/core';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSnackBarConfig, MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { UrlSerializer } from '@angular/router';
import { provideTranslocoMessageformat } from '@jsverse/transloco-messageformat';
import { NgIdleKeepaliveModule } from '@ng-idle/keepalive';
import { OktaAuth, OktaAuthOptions, StorageType } from '@okta/okta-auth-js';
import {
	NgHttpCachingModule,
	NgHttpCachingConfig,
	NgHttpCachingStrategy,
	NgHttpCachingLocalStorage
} from 'ng-http-caching';
import { provideNgxWebstorage } from 'ngx-webstorage';

import { DevicesDataAccessModule, SystemService } from '@shure/cloud/device-management/devices/data-access';
import { ApolloSysApiCloudModule } from '@shure/cloud/shared/apollo';
import { CloudDeviceBehaviorPluginsModule } from '@shure/cloud/shared/device-behavior';
import { FeatureRequestApiModule } from '@shure/cloud/shared/feature-request/data-access';
import { UserAgGridPreferencesService } from '@shure/cloud/shared/feature-user-app-preferences';
import { AppEnvironmentGuard } from '@shure/cloud/shared/guards/app-environment-guard';
import { MaintenanceApiModule } from '@shure/cloud/shared/maintenance/data-access';
import { provideMaintenanceModeInitializer } from '@shure/cloud/shared/maintenance/utils';
import { NotificationsBellIconMainComponent } from '@shure/cloud/shared/notifications/feature-notifications-bell-icon';
import { NotificationsDataAccessApiModule } from '@shure/cloud/shared/notifications/notifications-data-access';
import { OktaInterfaceService, OktaModule } from '@shure/cloud/shared/okta/data-access';
import { OrganizationsStoreService } from '@shure/cloud/shared/services/organizations-store-service';
import { HeaderToolbarModule } from '@shure/cloud/shared/ui/components';
import { UsersApiModule } from '@shure/cloud/shared/users/data-access';
import { AnalyticsService } from '@shure/cloud/shared/utils/analytics';
import { APP_ENVIRONMENT, BELL_ICON_COMPONENT_TOKEN, getShureCloudUri } from '@shure/cloud/shared/utils/config';
import { CookiebotService } from '@shure/cloud/shared/utils/cookiebot';
import { FEATURE_FLAG_SERVICE, initializeFeatureFlagsService } from '@shure/cloud/shared/utils/feature-flags';
import { ApiBaseurlInterceptor, ApiUrlConfig, AuthTokenInterceptor, HttpModule } from '@shure/cloud/shared/utils/http';
import { VerificationCodeApiModule } from '@shure/cloud/shared/verification-code/data-access';
import { PRISM_SNACKBAR_CONFIG, PrismBannerComponent, PrismSnackbarComponent } from '@shure/prism-angular-components';
import { StatsigProvider, provideStatsigFeatureFlags } from '@shure/shared/angular/utils/feature-flags/statsig';
import {
	TranslocoRootModule,
	provideTranslationScopeLoaderInitializer,
	provideTranslocoInitializer
} from '@shure/shared/angular/utils/i18n';
import { DEFAULT_APP_LANGUAGES } from '@shure/shared/angular/utils/i18n-app-defaults';
import { ConsoleLoggerSink, LoggingModule, StaticLoggingConfigProvider } from '@shure/shared/angular/utils/logging';
import { LowercaseUrlSerializer } from '@shure/shared/angular/utils/url-serializers';

import { environment } from '../environments/environment';
import { MaterialIconsModule } from '../modules/material-icons.module';
import { CloudSystemService } from '../services/system';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

const appBaseURL = window.location.origin;
const shureCloudUriMap = environment.shureCloudUriMap;

const oktaDomain =
	appBaseURL.includes('services') || appBaseURL.includes('localhost')
		? environment.authorization.oktaDomain
		: environment.authorization.oktaDomainExt;

const syncStorage = environment.cdmFeatureFlags?.showOrgSwitcher ? false : true;
const storageTypes: StorageType[] = environment.cdmFeatureFlags?.showOrgSwitcher
	? ['sessionStorage']
	: ['localStorage'];

const oktaConfig: OktaAuthOptions = {
	issuer: 'https://' + oktaDomain + environment.authorization.authServerPath,
	clientId: environment.authorization.clientId,
	redirectUri: `${appBaseURL}/signin/callback`,
	scopes: environment.authorization.scopes,
	logoutUrl: getShureCloudUri(appBaseURL, shureCloudUriMap),
	postLogoutRedirectUri: getShureCloudUri(appBaseURL, shureCloudUriMap),
	pkce: true,
	tokenManager: {
		autoRenew: true,
		syncStorage: syncStorage
	},
	storageManager: {
		token: {
			storageTypes: storageTypes
		},
		cache: {
			storageTypes: storageTypes
		},
		transaction: {
			storageTypes: storageTypes
		}
	}
};
const oktaAuth = new OktaAuth(oktaConfig);

const apiUrlConfig: ApiUrlConfig = {
	apiBaseUrl: environment.orgs?.orgsApiUrl,
	rolesApiUrl: environment.orgs?.rolesApiUrl
};

const cacheApiUrls = ['/permissions', '/version', '/contactdetails', '/organizationIds'];
const ngHttpCachingConfig: NgHttpCachingConfig = {
	lifetime: 1000 * 360, // cache expire after 6 mins
	allowedMethod: ['GET'],
	cacheStrategy: NgHttpCachingStrategy.ALLOW_ALL,
	store: new NgHttpCachingLocalStorage(),
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	isCacheable: (req: HttpRequest<any>): boolean | undefined => {
		//Caching the response of the cacheApiUrls
		for (const url of cacheApiUrls) {
			if (req.urlWithParams.indexOf(url) !== -1) {
				return true;
			}
		}
		// by returning "false" for all the other APIs
		return false;
	}
};

// TODO
// - Logging Sink

@NgModule({
	declarations: [AppComponent],
	bootstrap: [AppComponent],
	imports: [
		BrowserAnimationsModule,
		FormsModule,
		MatSidenavModule,
		MaterialIconsModule,
		TranslocoRootModule.forRoot(DEFAULT_APP_LANGUAGES, false),
		ReactiveFormsModule,
		OktaModule.forRoot(oktaAuth),
		NgIdleKeepaliveModule.forRoot(),
		ApolloSysApiCloudModule.forRoot(),
		DevicesDataAccessModule,
		AppRoutingModule,
		PrismBannerComponent,
		PrismSnackbarComponent,
		LoggingModule.forRoot(
			environment.production ? [ConsoleLoggerSink] : [ConsoleLoggerSink],
			{ base: { default: environment.logLevel } },
			StaticLoggingConfigProvider
		),
		CloudDeviceBehaviorPluginsModule,
		HeaderToolbarModule,
		NgHttpCachingModule.forRoot(ngHttpCachingConfig),
		MaintenanceApiModule.forRoot({ rootUrl: environment.orgs?.maintenanceApiUrl }),
		FeatureRequestApiModule.forRoot({ rootUrl: environment.orgs?.featureRequestApiUrl }),
		VerificationCodeApiModule.forRoot({ rootUrl: environment.orgs?.verificationCodeApiUrl }),
		HttpModule.forRoot(apiUrlConfig, [
			{
				provide: HTTP_INTERCEPTORS,
				useClass: ApiBaseurlInterceptor,
				multi: true
			},
			{
				provide: HTTP_INTERCEPTORS,
				useClass: AuthTokenInterceptor,
				multi: true
			}
		]),
		UsersApiModule.forRoot({
			rootUrl: environment.orgs?.usersApiUrl
		}),
		NotificationsDataAccessApiModule.forRoot({
			rootUrl: environment.orgs?.notificationsApiUrl
		})
	],
	providers: [
		provideHttpClient(withInterceptorsFromDi()),
		provideNgxWebstorage(),
		{ provide: UrlSerializer, useClass: LowercaseUrlSerializer },
		{ provide: OktaInterfaceService },
		{ provide: OrganizationsStoreService },
		provideTranslocoInitializer(),
		provideTranslocoMessageformat(),
		provideTranslationScopeLoaderInitializer(),
		provideMaintenanceModeInitializer(),
		{ provide: APP_ENVIRONMENT, useValue: environment },
		{
			provide: MAT_RIPPLE_GLOBAL_OPTIONS,
			useValue: <RippleGlobalOptions>{
				disabled: true
			}
		},
		{
			provide: MAT_SNACK_BAR_DEFAULT_OPTIONS,
			useValue: <MatSnackBarConfig>{
				duration: 8000,
				verticalPosition: 'bottom',
				horizontalPosition: 'center',
				panelClass: ['mat-toolbar', 'mat-primary']
			}
		},
		{
			// eslint-disable-next-line max-len
			// http://shure-prism-web-components.dev-aws-pr-ms-influx.shure.com/?path=/docs/components-snackbar-snackbar-in-an-angular-app-prism-snackbar-service--docs
			provide: PRISM_SNACKBAR_CONFIG,
			useValue: {
				duration: 8000
			}
		},
		{ provide: BELL_ICON_COMPONENT_TOKEN, useValue: NotificationsBellIconMainComponent },
		AppEnvironmentGuard,
		{
			provide: SystemService,
			useClass: CloudSystemService
		},
		AnalyticsService,
		CookiebotService,
		provideStatsigFeatureFlags({
			clientKey: environment.statsigConfig?.clientKey ?? '',
			tier: environment.statsigConfig?.tier ?? '',
			proxyApi: environment.statsigConfig?.proxyApi
		}),
		{
			provide: FEATURE_FLAG_SERVICE,
			useFactory: initializeFeatureFlagsService,
			deps: [StatsigProvider]
		},
		UserAgGridPreferencesService
	]
})
export class AppModule {}
